import {
  IUsageDataMRPCRequestParams,
  IUsageDataMRPCResponse,
} from 'multirpc-sdk';

import { MultiService } from 'modules/api/MultiService';
import { createNotifyingQueryFn } from 'store/utils/createNotifyingQueryFn';
import { createQuerySelectors } from 'store/utils/createQuerySelectors';
import { makeWorkerGatewayAuthorization } from 'domains/jwtToken/utils/makeWorkerGatewayAuthorization';
import { web3Api } from 'store/queries';

import { REFETCH_STATS_INTERVAL, statsTimeframeToIntervalMap } from '../const';
import { getStatsTimeBorders } from '../utils/getStatsTimeBorders';
import { IFetchPublicStatsParams } from './fetchPublicStats';
import { filterByPriorityPairs } from '../utils/filterByPriorityPairs';
import { featureConfig } from '../../common/featureConfig';
import { fetchStatisticsMock } from '../mocks/fetchStatisticsMock';

export interface IFetchPrivateStatsParams extends IFetchPublicStatsParams {
  includeTrafficTimeline?: boolean;
  tokens?: IUsageDataMRPCRequestParams['api_key_values'];
  unfiltered?: IUsageDataMRPCRequestParams['unfiltered'];
  workerToken: string;
}

// The endpoint name is listed in endpointsSerializedByParams constant
// in packages/protocol/src/store/queries/index.ts file.
// If the name has changed it should be reflected there as well.
export const {
  endpoints: { fetchPrivateStats },
  useFetchPrivateStatsQuery,
} = web3Api.injectEndpoints({
  endpoints: build => ({
    fetchPrivateStats: build.query<
      IUsageDataMRPCResponse,
      IFetchPrivateStatsParams
    >({
      keepUnusedDataFor: REFETCH_STATS_INTERVAL,
      queryFn: createNotifyingQueryFn(
        async ({
          blockchains,
          groupBy,
          includeTrafficTimeline,
          limit,
          metrics,
          timeframe,
          timeframeShift,
          tokens,
          unfiltered,
          workerToken,
        }) => {
          if (featureConfig.offlineTesting) {
            return { data: await fetchStatisticsMock() };
          }

          makeWorkerGatewayAuthorization(workerToken);

          const { end, start } = getStatsTimeBorders({
            timeframe,
            timeframeShift,
          });
          const interval = statsTimeframeToIntervalMap[timeframe];
          const intervalInSeconds = `${interval}s`;

          const api = unfiltered
            ? MultiService.getService().getPublicGateway()
            : MultiService.getService().getWorkerGateway();

          const data = await api.getSharkTelemetry(
            {
              api_key_values: tokens,
              blockchains,
              end,
              group_by: groupBy,
              include_top_ofs: metrics,
              include_traffic_timeline: includeTrafficTimeline,
              interval: intervalInSeconds,
              start,
              top_limit: limit,
            },
            { unfiltered },
          );

          return {
            data: {
              ...data,
              tops: filterByPriorityPairs(data.tops),
            },
          };
        },
      ),
    }),
  }),
  overrideExisting: true,
});

const fetchPrivateStatsFallback: IUsageDataMRPCResponse = {
  all_time_total: 0,
  request_timelines: [],
  tops: [],
  total: 0,
  total_bytes: '0',
};

export const {
  selectDataWithFallbackCachedByParams: selectPrivateStats,
  selectLoadingCachedByParams: selectPrivateStatsLoading,
  selectStateCachedByParams: selectPrivateStatsState,
} = createQuerySelectors({
  endpoint: fetchPrivateStats,
  fallback: fetchPrivateStatsFallback,
});
